import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = (form.querySelector('[name="contact_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      return;
    }

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const res = await fetch('https://readdy.ai/api/form/d917aqi181e9e92u9ac0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });

      if (res.ok) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const quickLinks = [
    { name: 'PandabuySpreadsheet Guide', path: '/pandabuy-spreadsheet-guide' },
    { name: 'Shoes Spreadsheet', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
    { name: 'Hoodies Spreadsheet', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
    { name: 'Accessories Spreadsheet', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  ];

  const socialLinks = [
    { name: 'Pandabuy Sheet', url: 'https://kaspreadsheet.com' },
    { name: 'Buy It Now', url: 'https://kaspreadsheet.com' },
  ];

  return (
    <footer className="bg-accent-900 text-background-50/80">
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-16 md:pt-20 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-accent-500 flex items-center justify-center">
                <i className="ri-file-list-3-line text-background-50 text-lg"></i>
              </div>
              <span className="text-lg font-semibold text-background-50 font-heading tracking-tight">
                pandabuy<span className="text-accent-400">.living</span>
              </span>
            </div>
            <p className="text-sm text-background-50/50 leading-relaxed mb-6">
              Your trusted source for curated pandabuyspreadsheet collections. Discover the best fashion, shoes, and accessories finds updated daily.
            </p>
            <div className="flex items-center gap-3">
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="w-9 h-9 rounded-lg bg-accent-800 flex items-center justify-center hover:bg-accent-500 transition-colors cursor-pointer">
                <i className="ri-external-link-line text-background-50 text-sm"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-background-50/40 uppercase tracking-widest mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  {'path' in link ? (
                    <Link to={link.path} className="text-sm text-background-50/60 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">
                      {link.name}
                    </Link>
                  ) : (
                    <a href={link.url} target="_blank" rel="noopener nofollow" className="text-sm text-background-50/60 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">
                      {link.name}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-background-50/40 uppercase tracking-widest mb-5">Main Platform</h4>
            <ul className="space-y-3">
              {socialLinks.map((link) => (
                <li key={link.name}>
                  <a href={link.url} target="_blank" rel="noopener nofollow" className="text-sm text-background-50/60 hover:text-accent-400 transition-colors whitespace-nowrap cursor-pointer">
                    {link.name} →
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-semibold text-background-50/40 uppercase tracking-widest mb-5">Subscribe</h4>
            <p className="text-sm text-background-50/50 mb-4 leading-relaxed">
              Get the latest pandabuyspreadsheet updates and new finds delivered to your inbox.
            </p>
            <form
              data-readdy-form=""
              onSubmit={handleSubmit}
              className="flex flex-col gap-3"
            >
              <div className="hidden-field">
                <input
                  type="text"
                  name="contact_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  required
                  className="flex-1 px-4 py-2.5 rounded-lg bg-accent-800 border border-accent-700 text-background-50 text-sm placeholder:text-background-50/30 focus:outline-none focus:border-accent-400 transition-colors"
                />
                <button
                  type="submit"
                  className="px-4 py-2.5 rounded-lg bg-accent-500 text-background-50 hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-send-plane-line text-sm"></i>
                </button>
              </div>
              {status === 'success' && (
                <p className="text-xs text-accent-400">Subscribed successfully! Welcome aboard.</p>
              )}
              {status === 'error' && (
                <p className="text-xs text-red-400">Something went wrong. Please try again.</p>
              )}
            </form>
          </div>
        </div>

        <div className="border-t border-accent-800 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-background-50/30">
            &copy; {new Date().getFullYear()} pandabuy.living &mdash; Curated PandabuySpreadsheet Collection
          </p>
          <div className="flex items-center gap-4">
            <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-xs text-background-50/30 hover:text-background-50/50 transition-colors cursor-pointer">
              Pandabuy Sheet
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}